import { flexRender, Table } from '@tanstack/react-table';
import { RefObject } from 'react';

import { fontStyles } from '../../../styles/typography';
import { classNames } from '../../../utils/classnames';
import { TokenSearchInput } from './Cells/TokenSearchInput';
import { STICKY_FIRST_CELL_BG_CLASSES } from './stickyFirstCell';

type Props<TData> = {
  table: Table<TData>;
  /** When true, this header is being rendered inside the sticky overlay. */
  isSticky: boolean;
  /** Controlled value of the token search input. */
  searchInputValue: string;
  /** Called when the user types in the search input. */
  onSearchInputChange: (value: string) => void;
  /** Called when the user clears the search via the X button or Escape. */
  onClearSearch: () => void;
  /**
   * Ref forwarded to the search input. The parent provides separate refs for
   * the regular and sticky headers so it can focus whichever is on screen.
   */
  searchInputRef: RefObject<HTMLInputElement | null>;
  /**
   * Placeholder text for the token search input. The parent derives this
   * from the active preset so the input reads "Search popular tokens",
   * "Search trending tokens", and so on.
   */
  searchPlaceholder?: string;
};

/**
 * Renders the discovery table's `<thead>`. Used twice on the homepage:
 * once for the regular table and once for the sticky overlay that appears
 * when the user scrolls past the table top. The token column is replaced by
 * a search input in both variants so users can filter the current category
 * by name or symbol regardless of scroll state.
 */
export const DiscoveryTableHeader = <TData,>({
  table,
  isSticky,
  searchInputValue,
  onSearchInputChange,
  onClearSearch,
  searchInputRef,
  searchPlaceholder,
}: Props<TData>) => (
  <thead>
    {table.getHeaderGroups().map((headerGroup) => (
      <tr key={headerGroup.id}>
        {headerGroup.headers.map((header) => {
          const sortDirection = header.column.getIsSorted();
          const isTokenColumn = header.column.id === 'token';

          return (
            <th
              key={header.id}
              aria-sort={
                sortDirection === 'asc'
                  ? 'ascending'
                  : sortDirection === 'desc'
                    ? 'descending'
                    : undefined
              }
              colSpan={header.colSpan}
              style={{ width: header.getSize() }}
              className={classNames(
                fontStyles({
                  variant: 'LABEL_SM',
                  class: ['text-token-gray-800', 'text-left'],
                }),
                'h-12',
                // In the sticky overlay we collapse padding so the row stays at
                // its 48px height even with the input inside the token column.
                // In the regular header we keep `pt-2` so the search input's
                // baseline aligns with the other column titles.
                isSticky ? 'py-0' : 'pt-2',
                'border-b',
                'border-solid',
                'border-token-alphalight-200',
                // No per-cell `bg-special-background-card`; the enclosing
                // <table> already paints the card surface, so applying it
                // again here would double-composite the translucent token in
                // dark mode and make these cells visibly lighter than the
                // first (opaque) column.
                'first:pl-4',
                'first:sticky',
                'first:z-20',
                'duration-0',
                ...STICKY_FIRST_CELL_BG_CLASSES,
                'group-data-[did-scroll=true]/wrapper:first:after:shadow-10',
                'group-data-[did-scroll=true]/wrapper:first:after:absolute',
                'group-data-[did-scroll=true]/wrapper:first:after:inset-0',
                'left-0',
                'tablet:last:pr-11',
                'last:pr-4',
                'whitespace-nowrap',
                'px-1',
                'align-middle',
              )}
            >
              {isTokenColumn ? (
                <TokenSearchInput
                  ref={searchInputRef}
                  value={searchInputValue}
                  onChange={onSearchInputChange}
                  onClear={onClearSearch}
                  placeholder={searchPlaceholder}
                />
              ) : header.isPlaceholder ? null : (
                flexRender(header.column.columnDef.header, header.getContext())
              )}
            </th>
          );
        })}
      </tr>
    ))}
  </thead>
);
